// SPDX-License-Identifier: Elastic-2.0
/**
 * Modal — accessible dialog primitive.
 *
 * Renders nothing while `open` is false (no portal, no listeners).
 * When open:
 *  - Portals into `document.body` so transforms / overflow on the
 *    React tree can't clip it.
 *  - Traps focus inside the card via `useFocusTrap` and restores focus
 *    to the trigger on close.
 *  - Esc closes (when `dismissable`).
 *  - Click on the backdrop closes (when `dismissable`); click inside the
 *    card does not.
 *  - Locks `<body>` scroll while open and restores the previous overflow.
 *
 * ARIA: `role="dialog"`, `aria-modal="true"`, `aria-labelledby` points at
 * the rendered `<h2>` so screen readers announce the title.
 */
import type { ReactNode } from "react";
import { useEffect, useId } from "react";
import { createPortal } from "react-dom";

import { useFocusTrap } from "@/lib/useFocusTrap";

import styles from "./Modal.module.css";

export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  /** Controls visibility. When false, nothing renders. */
  open: boolean;
  /** Called when the user requests close (Esc, backdrop, close button). */
  onClose: () => void;
  /** Dialog title — also wired to `aria-labelledby` for screen readers. */
  title: string;
  /** Dialog body. */
  children: ReactNode;
  /** Optional footer slot, typically a row of Buttons. */
  footer?: ReactNode;
  /**
   * Whether the user can dismiss via Esc / backdrop / close button.
   * Default true. Set false for confirm-required flows.
   */
  dismissable?: boolean;
  /** Width preset. Default `"md"`. */
  size?: ModalSize;
}

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: styles.sizeSm ?? "",
  md: styles.sizeMd ?? "",
  lg: styles.sizeLg ?? "",
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  dismissable = true,
  size = "md",
}: ModalProps): JSX.Element | null {
  const titleId = useId();
  const containerRef = useFocusTrap<HTMLDivElement>(open);

  // Esc-to-close.
  useEffect(() => {
    if (!open || !dismissable) return;
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, dismissable, onClose]);

  // Lock body scroll while open. Save + restore the previous value so
  // we don't fight an outer layout that might already be locking.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  const cardClasses = [styles.card, SIZE_CLASS[size]].filter(Boolean).join(" ");

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(event) => {
        // Only close when the mousedown started on the backdrop itself —
        // prevents accidental dismissal when a drag ends outside the card.
        if (dismissable && event.target === event.currentTarget) {
          onClose();
        }
      }}
      data-testid="modal-backdrop"
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cardClasses}
        data-testid="modal-card"
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          {dismissable ? (
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          ) : null}
        </div>
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
